import { FC, memo, useMemo } from 'react'
import { FlexboxGrid } from 'rsuite'
import CloseIcon from '@rsuite/icons/Close'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { AppliedFilters } from '../../utils/types/misc'
import FilterTag from './FilterTag'
import { MATERIAL_FILTER, getCategoryFilters, STANDARD_TEST_FILTERS, SUPPLIER_FILTER, TOP_LEVEL_FILTERS } from '../../lib/catalogFilter'
import useCategoryDataStructure from '../hooks/useCategoryDataStructure'
import { getCountryAndLocaleStrings } from '../../utils/locales'

import styles from '../../styles/FilterTags.module.less'

interface FilterTagsProps {
  appliedFilters: AppliedFilters
  handleClearAllFilters: () => void
  removeAppliedFilterItem: (filterKey: string, value: string | number) => void
}

const FilterTags: FC<FilterTagsProps> = (props) => {
  const {
    appliedFilters,
    handleClearAllFilters,
    removeAppliedFilterItem,
  } = props

  const { t } = useTranslation('modals')
  const { locale: countryAndLocale } = useRouter()
  const { categoryWithFilters } = useCategoryDataStructure()

  const { actualFilters, moreFilters } = getCategoryFilters(categoryWithFilters)
  const { locale: lang } = getCountryAndLocaleStrings(countryAndLocale)

  // Build filterKeyToAttributeMap for all filters
  const filterKeyToAttributeMap: Record<string, string> = useMemo(() => ({
    // Top-level filters
    ...TOP_LEVEL_FILTERS.reduce((acc, { id, typeSenseFieldName }) => {
      acc[`f_${id}`] = typeSenseFieldName === 'use-cases' ? `use-cases_${lang}` : typeSenseFieldName
      return acc
    }, {} as Record<string, string>),
    // Supplier filter
    ...SUPPLIER_FILTER.reduce((acc, { id }) => {
      acc[`f_${id}`] = 'supplier'
      return acc
    }, {} as Record<string, string>),
    // Color/material filters
    ...MATERIAL_FILTER.reduce((acc, { id }) => {
      acc[`f_${id}`] = `${id}.${lang}`
      return acc
    }, {} as Record<string, string>),
    // Standard test filters
    ...STANDARD_TEST_FILTERS.reduce((acc, { baseStandardCode }) => {
      acc[`f_${baseStandardCode}`] = 'standards.test'
      return acc
    }, {} as Record<string, string>),
    // Dynamic filters from actualFilters and moreFilters
    ...[...(actualFilters || []), ...(moreFilters || [])].reduce((acc, filter) => {
      if (filter['attribute.code']) {
        const typeName = filter['attribute.label']
        acc[`f_${filter['attribute.code'].split('/').pop()}`] = typeName === 'Color' ? `color.${lang}` : `feature-types.${lang}.${typeName}`
      }
      return acc
    }, {} as Record<string, string>),
  }), [actualFilters, moreFilters, lang])

  if (!Object.keys(appliedFilters).length) {
    return null
  }

  return (
    <FlexboxGrid className={styles['filter-tags']}>
      {Object.entries(appliedFilters).map(([filterKey, filters]) => filters.map((filter) => (
        <FilterTag
          key={filter.label}
          filter={filter}
          filterKey={filterKey}
          removeAppliedFilterItem={removeAppliedFilterItem}
          filterKeyToAttributeMap={filterKeyToAttributeMap}
        />
      )))}
      <FlexboxGrid
        align="middle"
        className={styles['filter-tag']}
      >
        <p className="margin-right-spacer">{t('modals:Clear All')}</p>
        <button
          onClick={handleClearAllFilters}
          type="button"
        >
          <CloseIcon
            width={12}
            height={12}
          />
        </button>
      </FlexboxGrid>
    </FlexboxGrid>
  )
}

export default memo(FilterTags)
